import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Building2, MapPin, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/**
 * Combined Customer/Site searchable dropdown
 * 
 * @param {Object} props
 * @param {Array} props.clients - List of customers/clients
 * @param {Array} props.sites - List of sites
 * @param {string} props.value - Selected value (format: "customer:id" or "site:id", or just site ID if siteOnly=true)
 * @param {function} props.onValueChange - Callback when value changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {boolean} props.siteOnly - If true, only allows site selection and returns just the site ID (not "site:id")
 * @param {boolean} props.showAllOption - If true, shows "All" option (default true for filter mode, false for siteOnly)
 */
export default function SiteCustomerCombobox({
  clients = [],
  sites = [],
  value,
  onValueChange,
  placeholder = "Search customers & sites...",
  className,
  disabled = false,
  siteOnly = false,
  showAllOption,
  'data-testid': dataTestId = "site-customer-combobox"
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Default showAllOption based on mode
  const shouldShowAllOption = showAllOption !== undefined ? showAllOption : !siteOnly;

  // Parse the current value - handle both modes
  const selectedType = siteOnly ? 'site' : value?.split(':')[0]; // 'customer' or 'site'
  const selectedId = siteOnly ? value?.toString() : value?.split(':')[1];

  // Get display label for current selection
  const displayLabel = useMemo(() => {
    if (!value || value === 'all') return null;
    
    if (siteOnly) {
      // In siteOnly mode, value is just the site ID
      const site = sites.find(s => s.id.toString() === value?.toString());
      if (site) {
        const customer = clients.find(c => c.id === site.parent_client_id || c.id === site.independent_client_id);
        return customer ? `${customer.name} - ${site.name}` : site.name;
      }
      return null;
    }
    
    if (selectedType === 'customer') {
      const customer = clients.find(c => c.id.toString() === selectedId);
      return customer ? `${customer.name} (All Sites)` : null;
    } else if (selectedType === 'site') {
      const site = sites.find(s => s.id.toString() === selectedId);
      if (site) {
        const customer = clients.find(c => c.id === site.parent_client_id || c.id === site.independent_client_id);
        return customer ? `${site.name} • ${customer.name}` : site.name;
      }
    }
    return null;
  }, [value, selectedType, selectedId, clients, sites, siteOnly]);

  // Get color for current selection
  const selectedColor = useMemo(() => {
    if (!value || value === 'all') return null;
    
    if (siteOnly) {
      const site = sites.find(s => s.id.toString() === value?.toString());
      if (site) {
        const customer = clients.find(c => c.id === site.parent_client_id || c.id === site.independent_client_id);
        return customer?.colour;
      }
      return null;
    }
    
    if (selectedType === 'customer') {
      const customer = clients.find(c => c.id.toString() === selectedId);
      return customer?.colour;
    } else if (selectedType === 'site') {
      const site = sites.find(s => s.id.toString() === selectedId);
      if (site) {
        const customer = clients.find(c => c.id === site.parent_client_id || c.id === site.independent_client_id);
        return customer?.colour;
      }
    }
    return null;
  }, [value, selectedType, selectedId, clients, sites, siteOnly]);

  // Filter customers and sites based on search
  // Also filter out inactive customers (is_active=false or deleted_at is set)
  const filteredItems = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    
    // Filter to only ACTIVE customers (is_active !== false and no deleted_at)
    const activeClients = clients.filter(c => 
      c.is_active !== false && !c.deleted_at
    );
    
    // In siteOnly mode, don't filter customers (they won't be shown anyway)
    const filteredCustomers = siteOnly ? [] : activeClients.filter(c => 
      c.name.toLowerCase().includes(query)
    );
    
    // Filter sites: only include sites whose parent customer is active
    const filteredSites = sites.filter(s => {
      const customer = activeClients.find(c => c.id === s.parent_client_id || c.id === s.independent_client_id);
      // Skip sites with no active customer
      if (!customer) return false;
      const customerName = customer?.name || '';
      return s.name.toLowerCase().includes(query) || customerName.toLowerCase().includes(query);
    });

    return { customers: filteredCustomers, sites: filteredSites };
  }, [searchQuery, clients, sites, siteOnly]);

  const handleSelect = (type, id) => {
    if (type === 'all') {
      onValueChange('all');
    } else if (siteOnly && type === 'site') {
      // In siteOnly mode, return just the site ID
      onValueChange(id.toString());
    } else {
      onValueChange(`${type}:${id}`);
    }
    setOpen(false);
    setSearchQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between", className)}
          disabled={disabled}
          data-testid={dataTestId}
        >
          <div className="flex items-center gap-2 truncate">
            {selectedColor && (
              <div 
                className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                style={{ backgroundColor: selectedColor }}
              />
            )}
            <span className="truncate">
              {displayLabel || placeholder}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Type to search..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            
            {/* All option - only shown when shouldShowAllOption is true */}
            {shouldShowAllOption && (
              <>
                <CommandGroup>
                  <CommandItem
                    value="all"
                    onSelect={() => handleSelect('all', null)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        (!value || value === 'all') ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span>All Customers & Sites</span>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            
            {/* Customers - only shown in filter mode (not siteOnly) */}
            {!siteOnly && filteredItems.customers.length > 0 && (
              <CommandGroup heading="Customers">
                {filteredItems.customers.map((customer) => (
                  <CommandItem
                    key={`customer-${customer.id}`}
                    value={`customer:${customer.id}`}
                    onSelect={() => handleSelect('customer', customer.id)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        (selectedType === 'customer' && selectedId === customer.id.toString()) 
                          ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-2.5 h-2.5 rounded-full" 
                        style={{ backgroundColor: customer.colour || '#6B7280' }}
                      />
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{customer.name}</span>
                      <span className="text-xs text-muted-foreground">(All Sites)</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            
            {/* Sites grouped by customer */}
            {filteredItems.sites.length > 0 && (
              <CommandGroup heading="Sites">
                {filteredItems.sites.map((site) => {
                  const customer = clients.find(c => 
                    c.id === site.parent_client_id || c.id === site.independent_client_id
                  );
                  // Check if this site is selected - handle both modes
                  const isSelected = siteOnly 
                    ? value?.toString() === site.id.toString()
                    : (selectedType === 'site' && selectedId === site.id.toString());
                  return (
                    <CommandItem
                      key={`site-${site.id}`}
                      value={`site:${site.id}`}
                      onSelect={() => handleSelect('site', site.id)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2.5 h-2.5 rounded-full" 
                          style={{ backgroundColor: customer?.colour || '#6B7280' }}
                        />
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="truncate">{site.name}</span>
                        {customer && (
                          <span className="text-xs text-muted-foreground truncate">
                            • {customer.name}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
